import { z } from 'zod';

/**
 * Request/response schemas for the jobs surface (ARCHITECTURE_PLAN §9:
 * `GET /api/w/:slug/jobs?status`, `POST /api/w/:slug/jobs/:id/retry`). Single
 * source of truth shared by the Worker and the SPA (§3).
 *
 * Aligned with the `Job` / `JobFailure` interfaces in `src/repo/jobs.ts`.
 */

/** Job lifecycle statuses (migration 0005 CHECK constraint). */
export const JobStatusSchema = z.enum(['queued', 'processing', 'succeeded', 'failed', 'dead']);
export type JobStatus = z.infer<typeof JobStatusSchema>;

/** A background job as exposed to the SPA (tenant-visible fields only). */
export const JobSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  type: z.string(),
  payload: z.unknown(),
  idempotencyKey: z.string(),
  status: JobStatusSchema,
  attempts: z.number().int(),
  maxAttempts: z.number().int(),
  lastError: z.string().nullable(),
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
});
export type Job = z.infer<typeof JobSchema>;

/** A job failure (DLQ landing) as exposed to the SPA. */
export const JobFailureSchema = z.object({
  id: z.string(),
  jobId: z.string(),
  tenantId: z.string().nullable(),
  error: z.string(),
  payload: z.unknown(),
  failedAt: z.number().int(),
  retriedAt: z.number().nullable(),
  retriedByUserId: z.string().nullable(),
});
export type JobFailure = z.infer<typeof JobFailureSchema>;

/**
 * `GET /api/w/:slug/jobs` response. Uses the shared paginated envelope
 * (`items`/`nextCursor`, matching every other list endpoint) plus the DLQ
 * failure rows for the jobs on this page, which the UI renders inline.
 */
export const JobsListResponseSchema = z.object({
  items: z.array(JobSchema),
  failures: z.array(JobFailureSchema),
  nextCursor: z.string().nullable(),
});
export type JobsListResponse = z.infer<typeof JobsListResponseSchema>;

/**
 * `POST /api/w/:slug/jobs/:id/retry` response. The server resets the job
 * to `queued` and re-enqueues it.
 */
export const JobRetryResponseSchema = z.object({
  id: z.string(),
  status: z.literal('queued'),
});
export type JobRetryResponse = z.infer<typeof JobRetryResponseSchema>;
